import { useIonModal } from '@ionic/react'
import { OverlayEventDetail } from '@ionic/react/dist/types/components/react-component-lib/interfaces'

import { usePingUnit } from '@apps-common/apis/portal/control'
import { AccountChangeModal } from '@apps-common/apps/portal/pages/units/modals'
import {
  DetailsItem,
  DetailsItemAction,
  DetailsItemModalAction
} from '@apps-common/apps/portal/pages/units/pages/manage/components/details'
import { ImageComponent } from '@apps-common/components/common'
import { CompanySelectModal } from '@apps-common/components/modals'
import { InfoModal } from '@apps-common/components/modals/info-modal'
import { SiteDetailsModal } from '@apps-common/components/modals/site/SiteDetailsModal'
import { AppType } from '@apps-common/config/app-types'
import { CompanyType } from '@apps-common/config/company'
import { ModalDismiss } from '@apps-common/config/modal'
import { UNIT_INVALID_SERIAL_NO_VALUES } from '@apps-common/config/unit'
import { invalidateAllUnitQueries } from '@apps-common/helpers/invalidate-unit-queries'
import { toLocalTimestampStr } from '@apps-common/helpers/to-local-timestamp'
import { unitIsActive, unitIsCancelled, unitIsInactive } from '@apps-common/helpers/unit-helpers'
import { valueIsValidCheck, valueStringCheck } from '@apps-common/helpers/value-string-check'
import { modalSlideUpEnterAnimation, modalSlideUpLeaveAnimation } from '@apps-common/lib/modal-animations'
import { IUnitGenericDetailsModel } from '@apps-common/types/portal/unit'
import { useMemo } from 'react'

interface IProps {
  loading?: boolean
  unit?: IUnitGenericDetailsModel
}

export const UnitDetailsPortalBody: React.FC<IProps> = ({ loading, unit }) => {
  const {
    unitId,
    imageUrl,
    product,
    siteName,
    mCentre,
    dateLoaded,
    planName,
    bureau,
    techCompany,
    accountNo,
    mobileNo,
    imei,
    serialNo,
    dateDeleted
  } = unit || {}

  const isActive = unitIsActive(unit)
  const isInactive = unitIsInactive(unit)
  const isCancelled = unitIsCancelled(unit)

  const {
    isLoading: pingLoading,
    isFetching: pingFetching,
    isRefetching: pingRefetching,
    isError: pingError,
    data: pingData
  } = usePingUnit({
    config: {
      enabled: !!unit?.unitId && !isCancelled
    },
    model: {
      unitId: unit?.unitId || '',
      triggerType: '',
      onTime: 0,
      commands: []
    }
  })

  const deviceOnline = !pingLoading && !pingFetching && !pingRefetching && !pingError && pingData?.pingResult

  const [presentSiteDetails, dismissSiteDetails] = useIonModal(SiteDetailsModal, {
    componentProps: { unitId },
    onDismiss: (data: string, role: string) => dismissSiteDetails(data, role),
    enableBackdropDismiss: false
  })

  const [presentTechChange, dismissTechChange] = useIonModal(CompanySelectModal, {
    componentProps: {
      subtitle: 'Update',
      title: 'Technician',
      companyType: CompanyType.Technician,
      noResultsDescription: 'Search above for technician companies'
    },
    onDismiss: (data: string, role: string) => dismissTechChange(data, role),
    enableBackdropDismiss: false
  })

  const accountModalOpts = useMemo(() => {
    if (deviceOnline) {
      return {
        modal: AccountChangeModal,
        props: { unitId }
      }
    }

    return {
      modal: InfoModal,
      props: {
        subtitle: 'Device status',
        title: 'No connection',
        body: 'The device must be online to change the account number. Please go back and ping the device to confirm it is online before proceeding.'
      }
    }
  }, [deviceOnline])

  return (
    <div className='grid grid-cols-2 sm:grid-cols-1 lg:grid-cols-2 gap-[15px] md:gap-3 lg:gap-6'>
      <div className='hidden sm:flex relative left-[-10px]'>
        {loading && (
          <div className='bg-skeleton rounded-full flex items-center justify-start gap-[10px] p-[5px] pr-[15px] animate-pulse'>
            <div className='bg-skeleton-hi p-[10px] rounded-full w-[50px] h-[50px]' />
            <div className='grow flex flex-col gap-[3px]'>
              <div className='bg-skeleton-hi w-[45px] h-[14px] rounded' />
              <div className='bg-skeleton-hi w-[65px] h-[18px] rounded' />
            </div>
          </div>
        )}
        {!loading && (
          <div className='bg-surface-2 border border-border rounded-full flex items-center justify-start gap-[10px] p-[5px] pr-[15px]'>
            <ImageComponent
              src={imageUrl}
              alt={product || 'Product name'}
              containerClasses='bg-text-muted/10 border border-border p-[10px] rounded-full w-[50px] h-[50px]'
              imageClasses='w-[32px] h-[32px]'
            />
            <div>
              <div className='text-text text-[0.8em] jost'>Product</div>
              <div className='text-text text-[0.9em] jost-light'>{product}</div>
            </div>
          </div>
        )}
      </div>
      <DetailsItemAction
        label='Site'
        value={valueStringCheck(siteName)}
        callbackHidden={isCancelled}
        allowCopy={!!siteName}
        callback={() =>
          presentSiteDetails({
            enterAnimation: modalSlideUpEnterAnimation,
            leaveAnimation: modalSlideUpLeaveAnimation,
            backdropDismiss: false,
            onWillDismiss: (ev: CustomEvent<OverlayEventDetail>) => {
              if (ev.detail.role === ModalDismiss.Confirm) {
                invalidateAllUnitQueries(unitId || '', AppType.portal)
              }
            }
          })
        }
        loading={loading}
      />
      {(isActive || isCancelled) && (
        <>
          <DetailsItem label='Monitoring centre' value={mCentre} loading={loading} />
          <DetailsItem label='Bureau' value={bureau} loading={loading} />
          <DetailsItemAction
            label='Technician'
            value={valueStringCheck(techCompany)}
            callbackHidden={isCancelled}
            callback={() =>
              presentTechChange({
                enterAnimation: modalSlideUpEnterAnimation,
                leaveAnimation: modalSlideUpLeaveAnimation,
                backdropDismiss: false,
                onWillDismiss: (ev: CustomEvent<OverlayEventDetail>) => {
                  if (ev.detail.role === ModalDismiss.Confirm) {
                    invalidateAllUnitQueries(unitId || '', AppType.portal)
                  }
                }
              })
            }
            loading={loading}
          />
        </>
      )}

      {isActive && (
        <>
          <DetailsItem label='Monitoring centre' value={mCentre} loading={loading} />
          <DetailsItem label='Bureau' value={bureau} loading={loading} />
          <DetailsItemAction
            label='Technician'
            value={valueStringCheck(techCompany)}
            callbackHidden={isCancelled}
            callback={() =>
              presentTechChange({
                enterAnimation: modalSlideUpEnterAnimation,
                leaveAnimation: modalSlideUpLeaveAnimation,
                backdropDismiss: false,
                onWillDismiss: (ev: CustomEvent<OverlayEventDetail>) => {
                  if (ev.detail.role === ModalDismiss.Confirm) {
                    invalidateAllUnitQueries(unitId || '', AppType.portal)
                  }
                }
              })
            }
            loading={loading}
          />
          <DetailsItem classes='hidden sm:block' label='Monthly plan' value={planName} loading={loading} />
          <DetailsItem
            classes='hidden sm:block'
            label='Activated on'
            value={toLocalTimestampStr(dateLoaded, 'DD MMM YY')}
            loading={loading}
          />
        </>
      )}
      {isInactive && (
        <>
          <DetailsItem
            classes='hidden sm:block'
            label='Registered on'
            value={toLocalTimestampStr(dateLoaded || '', 'DD MMM YY')}
            loading={loading}
          />
        </>
      )}
      {isCancelled && (
        <>
          <DetailsItem classes='hidden sm:block' label='Monthly plan' value={planName} loading={loading} />
          <DetailsItem
            classes='hidden sm:block'
            label='Cancelled on'
            value={toLocalTimestampStr(dateDeleted || '', 'DD MMM YY')}
            loading={loading}
          />
        </>
      )}
      <DetailsItemModalAction
        classes='hidden sm:block'
        label='Account number'
        value={valueStringCheck(isInactive ? null : accountNo)}
        Modal={accountModalOpts.modal}
        modalProps={accountModalOpts.props}
        modalSuccessCallback={() => invalidateAllUnitQueries(unitId || '', AppType.portal)}
        allowCopy
        callbackHidden={isCancelled}
        callbackDisabled={!isActive}
        loading={loading}
      />
      <DetailsItemAction loading={loading} label='Network' classes='hidden sm:block' value={valueStringCheck(unit?.networkType)} />
      <DetailsItem classes='hidden sm:block' label='Mobile number' value={mobileNo} allowCopy loading={loading} />
      <DetailsItem
        classes='hidden sm:block'
        label='IMEI number'
        value={valueStringCheck(imei)}
        allowCopy={valueIsValidCheck(imei)}
        loading={loading}
      />
      <DetailsItem
        classes='hidden sm:block'
        label='Serial'
        value={valueStringCheck(serialNo, UNIT_INVALID_SERIAL_NO_VALUES)}
        allowCopy={valueIsValidCheck(serialNo, UNIT_INVALID_SERIAL_NO_VALUES)}
        loading={loading}
      />
    </div>
  )
}
